import { Skeleton } from '@pay-com/components'
import { Logo } from '@pay-com/icons'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGenerateOnboardingPricingSigningUrlMutation } from 'generated/graphql'
import { DOCUSIGN_INTEGRATION_KEY, DOCUSIGN_JS_URL } from 'consts'
import {
  getLocalStorage,
  LocalStorageKeys,
  setLocalStorage
} from 'utils/local-storage'

type AmendmentProps = {
  pricingId: string
  taxSignerFullName: string
  onSigned: () => void
}

export const Amendment = ({
  pricingId,
  taxSignerFullName,
  onSigned
}: AmendmentProps) => {
  const { t } = useTranslation()
  const [status, setStatus] = useState<string | null>(null)

  const [generateSigningUrl, { data: urlData, loading: urlLoading, error }] =
    useGenerateOnboardingPricingSigningUrlMutation()

  const url = urlData?.generateOnboardingPricingSigningUrl?.url

  useEffect(() => {
    if (url) return

    generateSigningUrl({ variables: { input: { id: pricingId } } })
  }, [pricingId, generateSigningUrl, url])

  useEffect(() => {
    if (!url) return undefined

    let unmounted = false

    const scriptReady = window.DocuSign
      ? Promise.resolve()
      : new Promise<void>((resolve, reject) => {
          const script = document.createElement('script')
          script.src = DOCUSIGN_JS_URL
          script.onload = () => resolve()
          script.onerror = reject
          document.head.appendChild(script)
        })

    scriptReady
      .then(() => {
        const lib = window.DocuSign
        if (!lib) throw new Error('DocuSign library not loaded')
        return lib.loadDocuSign(DOCUSIGN_INTEGRATION_KEY)
      })
      .then(docusign => {
        if (unmounted) return

        const signing = docusign.signing({
          url,
          displayFormat: 'focused',
          style: {
            branding: {
              primaryButton: {
                backgroundColor: '#ADABFF',
                color: '#000000'
              }
            },
            signingNavigationButton: {
              finishText: t('amendment.signButton'),
              position: 'bottom-right'
            }
          }
        })

        signing.on('sessionEnd', event => {
          if (event.sessionEndType === 'signing_complete') {
            const current =
              getLocalStorage<string[]>(LocalStorageKeys.SignedPricings) ?? []
            if (!current.includes(pricingId)) {
              setLocalStorage(LocalStorageKeys.SignedPricings, [
                ...current,
                pricingId
              ])
            }
            onSigned()
          }
          setStatus(event.sessionEndType)
        })

        signing.mount('#docusign-container')
      })
      .catch(() => {
        if (!unmounted) setStatus('exception')
      })

    return () => {
      unmounted = true
    }
  }, [url, t, pricingId])

  if (
    error ||
    status === 'exception' ||
    status === 'session_timeout' ||
    status === 'ttl_expired'
  ) {
    throw new Error(`Failed to load signing page, reason: ${status || error}`)
  }

  return (
    <>
      <div className="fixed left-0 top-0 z-10 flex p-6">
        <Logo />
      </div>
      <div className="flex h-dvh flex-col bg-purple-50 py-8">
        <div className="flex min-h-0 w-full max-w-screen-2xl flex-1 flex-col gap-4 px-6 xl:flex-row 2xl:max-w-none">
          <aside className="flex w-full shrink-0 flex-col pt-16 xl:w-[490px]">
            <p className="mb-4 text-lg font-semibold text-blueGray-900">
              {t('amendment.greeting', { name: taxSignerFullName })}
            </p>
            <h1 className="mb-4 text-2.5xl font-bold text-blueGray-900">
              {t('amendment.title')}
            </h1>
            <p className="text-base text-gray-500">
              {t('amendment.description')}
            </p>
          </aside>

          <div className="min-h-0 w-full flex-1 bg-white xl:w-auto 2xl:mx-auto 2xl:max-w-5xl">
            {!status && (urlLoading || !url) && (
              <Skeleton className="size-full" />
            )}

            {!status && url && (
              <div
                id="docusign-container"
                className="size-full rounded-lg border border-gray-200 p-1"
              />
            )}
          </div>
        </div>
      </div>
    </>
  )
}
